import axios from 'axios';
import { configService } from '../services/configService';
import {
    emptyPensionSetupSnapshot,
    parsePensionSetupFromApi,
    type PensionSetupSnapshot,
} from '../utils/pensionOnboarding';


export const getStayBookings = async (proId: number, token: string | null) => {
    return await axios.get(configService.getFullApiUrl(`/boarding/pro/${proId}/stay-bookings`), {
        headers: { Authorization: `Bearer ${token}` }
    });
};

export const getStayBookingsByClient = async (clientId: number, token: string | null) => {
    return await axios.get(configService.getFullApiUrl(`/boarding/client/${clientId}/stay-bookings`), {
        headers: { Authorization: `Bearer ${token}` }
    });
};

export const getStayBookingsByAnimal = async (animalId: number, token: string | null) => {
    return await axios.get(configService.getFullApiUrl(`/boarding/animal/${animalId}/stay-bookings`), {
        headers: { Authorization: `Bearer ${token}` }
    });
};

// --- Calendar & Allocations ---
export const getBoardingCalendar = async (proId: number, startDate: string, endDate: string, token: string | null) => {
    return await axios.get(configService.getFullApiUrl(`/boarding/calendar/${proId}`), {
        params: { start_date: startDate, end_date: endDate },
        headers: { Authorization: `Bearer ${token}` }
    });
};

export const getBoardingAllocations = async (proId: number, startDate: string, endDate: string, token: string | null) => {
    return await axios.get(configService.getFullApiUrl(`/boarding/allocations/${proId}`), {
        params: { start_date: startDate, end_date: endDate },
        headers: { Authorization: `Bearer ${token}` }
    });
};

export const getBoardingSettings = async (proId: number, token: string | null) => {
    return await axios.get(configService.getFullApiUrl(`/boarding/settings/${proId}`), {
        headers: { Authorization: `Bearer ${token}` }
    });
};

export const updateBoardingSettings = async (data: any, token: string | null) => {
    return await axios.put(configService.getFullApiUrl(`/boarding/settings`), data, {
        headers: { Authorization: `Bearer ${token}` }
    });
};

// --- Bootstrap defaults (first-time setup) ---
export const bootstrapBoardingDefaults = async (proId: number, token: string | null) => {
    const { data } = await axios.post(
        configService.getFullApiUrl(`/boarding/bootstrap-defaults`),
        { pro_id: proId },
        { headers: { Authorization: `Bearer ${token}` } }
    );
    return data;
};

// --- Pools (Zones) ---
export const getBoardingPools = async (proId: number, token: string | null) => {
    return await axios.get(configService.getFullApiUrl(`/boarding/pools/pro/${proId}`), {
        headers: { Authorization: `Bearer ${token}` }
    });
};

export const createBoardingPool = async (data: any, token: string | null) => {
    return await axios.post(configService.getFullApiUrl(`/boarding/pools`), data, {
        headers: { Authorization: `Bearer ${token}` }
    });
};

export const updateBoardingPool = async (poolId: number, data: any, token: string | null) => {
    return await axios.put(configService.getFullApiUrl(`/boarding/pools/${poolId}`), data, {
        headers: { Authorization: `Bearer ${token}` }
    });
};

export const archiveBoardingPool = async (poolId: number, token: string | null) => {
    const { data } = await axios.post(
        configService.getFullApiUrl(`/boarding/pools/${poolId}/archive`),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
    );
    return data;
};

// --- Units (Box) ---
export const getBoardingUnits = async (poolId: number, token: string | null) => {
    return await axios.get(configService.getFullApiUrl(`/boarding/units/${poolId}`), {
        headers: { Authorization: `Bearer ${token}` }
    });
};

export const createBoardingUnit = async (data: any, token: string | null) => {
    return await axios.post(configService.getFullApiUrl(`/boarding/units`), data, {
        headers: { Authorization: `Bearer ${token}` }
    });
};

export const updateBoardingUnit = async (unitId: number, data: any, token: string | null) => {
    return await axios.put(configService.getFullApiUrl(`/boarding/unit/${unitId}`), data, {
        headers: { Authorization: `Bearer ${token}` }
    });
};

export const archiveBoardingUnit = async (unitId: number, token: string | null) => {
    const { data } = await axios.post(
        configService.getFullApiUrl(`/boarding/unit/${unitId}/archive`),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
    );
    return data;
};

// --- Open Periods ---
export const getBoardingPoolOpenPeriods = async (poolId: number, token: string | null) => {
    return await axios.get(configService.getFullApiUrl(`/boarding/pools/${poolId}/open-periods`), {
        headers: { Authorization: `Bearer ${token}` }
    });
};

export const addBoardingPoolOpenPeriod = async (data: { pool_id: number, start_date: string, end_date: string }, token: string | null) => {
    return await axios.post(configService.getFullApiUrl(`/boarding/pools/open-periods`), data, {
        headers: { Authorization: `Bearer ${token}` }
    });
};

export const deleteBoardingPoolOpenPeriod = async (periodId: number, token: string | null) => {
    return await axios.delete(configService.getFullApiUrl(`/boarding/pools/open-periods/${periodId}`), {
        headers: { Authorization: `Bearer ${token}` }
    });
};

export const assignUnitToStay = async (stayId: number, unitId: number, token: string | null) => {
    return await axios.post(configService.getFullApiUrl(`/boarding/stay-bookings/${stayId}/assign-unit`), { unit_id: unitId }, {
        headers: { Authorization: `Bearer ${token}` }
    });
};

export const updateStayBookingStatus = async (
    stayId: number,
    status: string,
    token: string | null,
    checkin_datetime?: string,
    checkout_datetime?: string
) => {
    return await axios.put(configService.getFullApiUrl(`/boarding/stay-bookings/${stayId}/status`), {
        status,
        checkin_datetime,
        checkout_datetime
    }, {
        headers: { Authorization: `Bearer ${token}` }
    });
};

// --- Police Register ---
export const searchPoliceRegister = async (proId: number, params: { query?: string, start_date?: string, end_date?: string }, token: string | null) => {
    return await axios.get(configService.getFullApiUrl(`/boarding/police-register/search/${proId}`), {
        params,
        headers: { Authorization: `Bearer ${token}` }
    });
};

export const exportPoliceRegister = (proId: number, token: string | null, params: { start_date?: string, end_date?: string, lang?: string }) => {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.lang) queryParams.append('lang', params.lang);
    if (token) queryParams.append('token', token); // Optional if using direct link or handling with blob

    return configService.getFullApiUrl(`/boarding/police-register/export/${proId}?${queryParams.toString()}`);
};

export const downloadStayContractPdf = async (stayId: number, token: string | null) => {
    const response = await axios.get(
        configService.getFullApiUrl(`/boarding/stay-bookings/${stayId}/contract.pdf`),
        {
            headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                Accept: 'application/pdf',
            },
            responseType: 'blob',
        },
    );
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `contrat_pension_${stayId}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

// --- Échéancier d'un séjour (acompte + solde) ---

export interface StayInstallment {
    installment_id: number;
    sort_order: number;
    label: string;
    amount_due: number;
    due_date: string | null;
    status: 'pending' | 'paid' | 'waived';
    hosted_invoice_url?: string | null;
}

export interface StayPaymentPlan {
    plan_id: number;
    contract_total_amount: number;
    paid_total: number;
    remaining: number;
    installments: StayInstallment[];
}

export const getStayPaymentPlan = async (
    stayId: number,
    token: string | null
): Promise<StayPaymentPlan | null> => {
    const response = await axios.get(
        configService.getFullApiUrl(`/boarding/stay-bookings/${stayId}/payment-plan`),
        { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data?.plan ?? null;
};

export const createStayPaymentPlan = async (
    stayId: number,
    deposit: { deposit_amount_eur?: number; deposit_percentage?: number },
    token: string | null
) => {
    return await axios.post(
        configService.getFullApiUrl(`/boarding/stay-bookings/${stayId}/payment-plan`),
        deposit,
        { headers: { Authorization: `Bearer ${token}` } }
    );
};

export const payStayInstallment = async (
    stayId: number,
    installmentId: number,
    payment: { amount_eur: number; method?: 'cash' | 'check' | 'transfer' | 'sepa' },
    token: string | null
) => {
    return await axios.post(
        configService.getFullApiUrl(
            `/boarding/stay-bookings/${stayId}/payment-plan/installments/${installmentId}/payment`
        ),
        payment,
        { headers: { Authorization: `Bearer ${token}` } }
    );
};

export const updateStayBooking = async (
    stayId: number,
    data: {
        checkin_time?: string,
        checkout_time?: string,
        // Déplacer un séjour : les jours réservés dans le box et le tarif
        // sont recalculés côté serveur.
        start_date?: string,
        end_date?: string,
        total_price_eur?: number,
        force_booking?: boolean,
    },
    token: string | null
) => {
    return await axios.put(configService.getFullApiUrl(`/boarding/stay-bookings/${stayId}`), data, {
        headers: { Authorization: `Bearer ${token}` }
    });
};

// --- Sanitary register (DDPP) ---
export type SanitaryEventType = 'medication' | 'treatment' | 'observation' | 'vaccination' | 'other';

export interface SanitaryEvent {
    entry_id: number;
    entry_number: number;
    entry_datetime: string;
    animal_id: number;
    stay_booking_id: number | null;
    event_type: SanitaryEventType;
    medication_name: string | null;
    dosage: string | null;
    administration_time: string;
    administration_route: string | null;
    administered_by_user_id: number;
    administered_by_name?: string | null;
    administered_by_lastname?: string | null;
    witnessed_by_user_id: number | null;
    witnessed_by_name?: string | null;
    witnessed_by_lastname?: string | null;
    prescribing_vet_name: string | null;
    prescription_date: string | null;
    prescription_number: string | null;
    notes: string | null;
    side_effects: string | null;
    is_correction: boolean;
    corrects_entry_id: number | null;
    correction_reason: string | null;
    correction_date: string | null;
    created_at: string;
    updated_at: string;
}

export const listStaySanitaryEvents = async (stayId: number, token: string | null) => {
    return await axios.get(configService.getFullApiUrl(`/boarding/stays/${stayId}/sanitary-events`), {
        headers: { Authorization: `Bearer ${token}` }
    });
};

export const createStaySanitaryEvent = async (
    stayId: number,
    payload: {
        event_type: SanitaryEventType;
        administration_time: string;
        medication_name?: string;
        dosage?: string;
        administration_route?: string;
        witnessed_by_user_id?: number;
        prescribing_vet_name?: string;
        prescription_date?: string;
        prescription_number?: string;
        notes?: string;
        side_effects?: string;
    },
    token: string | null,
) => {
    return await axios.post(
        configService.getFullApiUrl(`/boarding/stays/${stayId}/sanitary-events`),
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
    );
};

export const correctSanitaryEvent = async (
    entryId: number,
    payload: { correction_reason: string; new_values: Partial<SanitaryEvent> },
    token: string | null,
) => {
    return await axios.post(
        configService.getFullApiUrl(`/boarding/sanitary-events/${entryId}/correct`),
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
    );
};

// --- Pricing Rules ---
export const getBoardingPricingRules = async (proId: number, token: string | null) => {
    return await axios.get(configService.getFullApiUrl(`/boarding/pricing/rules/pro/${proId}`), {
        headers: { Authorization: `Bearer ${token}` }
    });
};

export const createBoardingPricingRule = async (data: any, token: string | null) => {
    return await axios.post(configService.getFullApiUrl(`/boarding/pricing/rules`), data, {
        headers: { Authorization: `Bearer ${token}` }
    });
};

export const updateBoardingPricingRule = async (ruleId: number, data: any, token: string | null) => {
    return await axios.put(configService.getFullApiUrl(`/boarding/pricing/rules/${ruleId}`), data, {
        headers: { Authorization: `Bearer ${token}` }
    });
};

export const deleteBoardingPricingRule = async (ruleId: number, token: string | null) => {
    return await axios.delete(configService.getFullApiUrl(`/boarding/pricing/rules/${ruleId}`), {
        headers: { Authorization: `Bearer ${token}` }
    });
};

export async function fetchPensionSetupSnapshot(
    proId: number,
    token: string | null,
    isBoardingEnabled: boolean
): Promise<PensionSetupSnapshot> {
    if (!isBoardingEnabled) {
        return emptyPensionSetupSnapshot(false);
    }
    try {
        const poolsRes = await getBoardingPools(proId, token);
        const poolsPayload = poolsRes.data;
        const listed = (poolsPayload?.pools ?? poolsPayload?.data?.pools ?? []) as Array<{
            pool_id?: number;
        }>;
        const pools = listed.filter((p) => Number(p?.pool_id) > 0);
        const unitChunks = await Promise.all(
            pools.slice(0, 20).map(async (p) => {
                try {
                    const uRes = await getBoardingUnits(Number(p.pool_id), token);
                    return (uRes.data?.units || []) as unknown[];
                } catch {
                    return [] as unknown[];
                }
            })
        );
        let stayCount = 0;
        try {
            const staysRes = await getStayBookings(proId, token);
            stayCount = parsePensionSetupFromApi({
                bookings: staysRes.data,
            }).stayCount;
        } catch {
            stayCount = 0;
        }
        const parsed = parsePensionSetupFromApi({
            pools: { pools },
            units: unitChunks.flat(),
        });
        return {
            isBoardingEnabled: true,
            poolCount: parsed.poolCount,
            unitCount: parsed.unitCount,
            stayCount,
        };
    } catch {
        return emptyPensionSetupSnapshot(true);
    }
}

